import PropTypes from 'prop-types';
import { isValidPrice } from '../../../../../../utilities/cmpPackagesUtils';
import { DATE_FORMATS, POLICY_TYPES } from '../../../../../../utilities/constants';
import { GwDatesWrapper } from '../../../../../_internal_date_/gwDatesWrapper';
import { getCancellationPolicyCopy } from '../../../../../PaymentForm/ReviewAndCompleteSection/utilities/helpers';
import { getIsOnlyOneFareFamilyRateCase } from '../../../../../SuiteCard/PriceSection/utilities/helpers';
import { Relative, Text } from '../../../../../ui-kit';
import theme from '../../../../../ui-kit/themes/theme';
import { editCopyByReplaceString } from '../../../../../utilities/copyFunctions';
import { handleDueAmounts, handleTotalWithTaxes } from '../../../../../utilities/utils';
import { SummaryContent } from '../../../Content';
import * as COPY from '../../utilities/copy';

const propTypes = {
  /** Due Amount for today */
  dueTodayAmount: PropTypes.number.isRequired,

  /** Due Amount at check in */
  dueAtCheckinAmount: PropTypes.number.isRequired
};

export const DueAmount = ({
  dueTodayAmount,
  dueAtCheckinAmount,
  roomAdjustmentTax,
  showLoyalty,
  loyaltyOptIn,
  pointAmount,
  suiteTotal,
  numberOfNights,
  isUserAuthenticated,
  userOffers,
  activeOffers,
  setExcedent,
  taxesAndFees,
  currentSelectedPackages,
  summaryTotal,
  arrival,
  selectedSuite,
  shouldSavePoints,
  isChangedToFlex,
  isCartHandoff
}) => {
  const shouldDisplay = isValidPrice(dueTodayAmount) && isValidPrice(dueAtCheckinAmount);
  const isOnlyOneFareFamilyRateCase = getIsOnlyOneFareFamilyRateCase(selectedSuite?.rateFamilies);
  const isRefundable = selectedSuite?.refundable;
  const arrivalDate = GwDatesWrapper.format(arrival, DATE_FORMATS.formatMonthDateNoDot);
  const tomorrowDate = GwDatesWrapper.format(
    GwDatesWrapper.add(GwDatesWrapper.today(), 1, 'days'),
    DATE_FORMATS.formatMonthDateNoDot
  );

  const isFareFamilyFourRatesCase = selectedSuite?.rateFamilies?.length > 1;
  const hasKnownCartPolicy =
    isCartHandoff && Object.values(POLICY_TYPES).includes(selectedSuite?.rateFamilies?.[0]?.policyType);
  const isPolicyTypeKnown = isFareFamilyFourRatesCase || hasKnownCartPolicy;

  //Get the correct copy for cancel policies
  //=========================================
  const getDepositCopy = () => {
    if (isOnlyOneFareFamilyRateCase) return '';
    return editCopyByReplaceString(
      isRefundable ? COPY.CANCEL_TEXT_FLEXIBLE : COPY.CANCEL_TEXT_STANDARD,
      isRefundable ? arrivalDate : tomorrowDate,
      '<DATE>'
    );
  };

  const fareFamilyFourRateCopy = getCancellationPolicyCopy({
    checkinDateSelection: arrival,
    isRefundable,
    isChangedToFlex,
    isPolicyTypeKnown
  });

  if (!shouldDisplay) return null;

  const handleSummaryTotal = isUserAuthenticated
    ? handleTotalWithTaxes(
        userOffers,
        activeOffers,
        showLoyalty,
        loyaltyOptIn,
        shouldSavePoints ? 0 : pointAmount,
        suiteTotal,
        taxesAndFees,
        roomAdjustmentTax,
        currentSelectedPackages,
        setExcedent
      )
    : summaryTotal.toFixed(2);

  const handleDueToday = isUserAuthenticated
    ? handleDueAmounts(
        dueTodayAmount,
        dueAtCheckinAmount,
        roomAdjustmentTax,
        showLoyalty,
        loyaltyOptIn,
        shouldSavePoints ? 0 : pointAmount,
        suiteTotal,
        numberOfNights,
        userOffers,
        activeOffers,
        handleSummaryTotal
      ).today
    : dueTodayAmount.toFixed(2);

  const handleDuetAtCheckIn = isUserAuthenticated
    ? handleDueAmounts(
        dueTodayAmount,
        dueAtCheckinAmount,
        roomAdjustmentTax,
        showLoyalty,
        loyaltyOptIn,
        shouldSavePoints ? 0 : pointAmount,
        suiteTotal,
        numberOfNights,
        userOffers,
        activeOffers,
        handleSummaryTotal
      ).atCheckIn
    : dueAtCheckinAmount.toFixed(2);

  return (
    <>
      <SummaryContent.Divider />
      <SummaryContent.Item fontSize={0} fontWeight="bold">
        <SummaryContent.Description lineHeight={'18px'}>{COPY.DUE_TODAY}</SummaryContent.Description>
        <SummaryContent.Amount width={'unset!important'}>{`$${handleDueToday}`}</SummaryContent.Amount>
      </SummaryContent.Item>
      <SummaryContent.Item>
        <Relative top={'-10px'}>
          <Text
            color={theme.colors.deepLakeBlue[100]}
            fontWeight={theme.fontWeights.medium}
            lineHeight={'18px'}
            fontSize={0}>
            {isPolicyTypeKnown ? fareFamilyFourRateCopy : getDepositCopy()}
          </Text>
        </Relative>
      </SummaryContent.Item>
      <SummaryContent.Item fontSize={0} fontWeight={theme.fontWeights.medium}>
        <SummaryContent.Description>{COPY.DUE_CHECKIN}</SummaryContent.Description>
        <SummaryContent.Amount width={'unset!important'}>{`$${handleDuetAtCheckIn}`}</SummaryContent.Amount>
      </SummaryContent.Item>
    </>
  );
};

DueAmount.propTypes = propTypes;
